import type { QueryClient } from "@tanstack/react-query";

import { LABOR_OPEN_SESSION_QUERY_KEY } from "@/components/LaborActiveSessionProvider";
import { readLaborDevShellLock } from "@/lib/labor/laborDevShellLock";
import type { LaborActiveSessionPayload, LaborSession } from "@/lib/labor/laborApi";
import type { LaborPresence } from "@/lib/labor/laborPresence";
import { resolveLaborPresence } from "@/lib/labor/laborPresence";
import { openSessionFromLaborPayload } from "@/lib/labor/parseLaborSession";

export function readOpenLaborSessionPayloadFromCache(
  queryClient: QueryClient,
): LaborActiveSessionPayload | undefined {
  return queryClient.getQueryData<LaborActiveSessionPayload>([...LABOR_OPEN_SESSION_QUERY_KEY]);
}

/** Keeps the hub-wide open-session React Query cache aligned with page-local labor state. */
export function writeOpenLaborSessionCache(queryClient: QueryClient, session: LaborSession | null): void {
  queryClient.setQueryData<LaborActiveSessionPayload>([...LABOR_OPEN_SESSION_QUERY_KEY], (current) => {
    if (current === undefined) {
      return { session };
    }
    return { ...current, session };
  });
}

export function resolvePresenceForNavigationSync(queryClient: QueryClient): {
  presence: LaborPresence | null;
  cacheLoaded: boolean;
} {
  const payload = readOpenLaborSessionPayloadFromCache(queryClient);
  if (payload === undefined) {
    return { presence: null, cacheLoaded: false };
  }
  return {
    presence: resolveLaborPresence({
      openSession: openSessionFromLaborPayload(payload),
      devShell: readLaborDevShellLock(),
      activeOnOtherPage: payload?.active_on_other_page,
      activeOnOtherSource: payload?.active_on_other_source,
    }),
    cacheLoaded: true,
  };
}
